// Digital signing for the liveness channel (Web Crypto), matching the backend
// services/liveness_crypto.py:
//   * client → server: send RSA-OAEP( SHA-256(data) ) under the server public key;
//   * server → client: verify the server's RSASSA-PKCS1-v1_5 signature over the data.
// The handshake (GET /liveness/) hands us the server public key.
import api from "lib/api";

const enc = new TextEncoder();

function pemToDer(pem) {
  const b64 = pem
    .replace(/-----BEGIN [^-]+-----/g, "")
    .replace(/-----END [^-]+-----/g, "")
    .replace(/\s+/g, "");
  const bin = atob(b64);
  const buf = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) buf[i] = bin.charCodeAt(i);
  return buf.buffer;
}

function toB64(buf) {
  const a = new Uint8Array(buf);
  let s = "";
  for (let i = 0; i < a.length; i++) s += String.fromCharCode(a[i]);
  return btoa(s);
}

function fromB64(s) {
  const bin = atob(s);
  const a = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) a[i] = bin.charCodeAt(i);
  return a.buffer;
}

// Import the server public key (SPKI PEM) twice: for OAEP encryption (our signing)
// and for RSASSA verification (checking the server's signatures).
export async function importServerKey(pem) {
  const der = pemToDer(pem);
  const [encKey, verifyKey] = await Promise.all([
    crypto.subtle.importKey("spki", der, { name: "RSA-OAEP", hash: "SHA-256" }, false, ["encrypt"]),
    crypto.subtle.importKey("spki", der, { name: "RSASSA-PKCS1-v1_5", hash: "SHA-256" }, false, ["verify"]),
  ]);
  return { encKey, verifyKey };
}

async function signBody(dataStr, encKey) {
  const hash = await crypto.subtle.digest("SHA-256", enc.encode(dataStr));
  const ct = await crypto.subtle.encrypt({ name: "RSA-OAEP" }, encKey, hash);
  return toB64(ct);
}

async function verifyServer(dataStr, sigB64, verifyKey) {
  return crypto.subtle.verify(
    { name: "RSASSA-PKCS1-v1_5" }, verifyKey, fromB64(sigB64), enc.encode(dataStr));
}

// POST a signed liveness envelope and verify the signed response. Returns the parsed
// server payload (with `_status`). Throws if the server response isn't a valid signed
// envelope (tamper / MITM / unsigned error like 404/409).
export async function postSigned(url, payloadObj, keys, opts = {}) {
  // A per-request timeout so a stalled connection rejects instead of hanging
  // forever (which would freeze the per-frame capture loop). Omitted = no timeout.
  const cfg = opts.timeout ? { timeout: opts.timeout } : undefined;
  // Unsigned mode: the server couldn't provide a key (infra) → plain request, so a
  // legit contestant is never blocked by an internal crypto problem.
  if (!keys) {
    let res;
    try {
      res = await api.post(url, payloadObj, cfg);
    } catch (e) {
      res = e?.response;
      if (!res) throw e;
    }
    const p = res.data || {};
    p._status = res.status;
    return p;
  }
  const data = JSON.stringify(payloadObj);
  const sig = await signBody(data, keys.encKey);
  let res;
  try {
    res = await api.post(url, { data, sig }, cfg);
  } catch (e) {
    res = e?.response;
    if (!res) throw e;
  }
  const body = res.data || {};
  if (typeof body.data !== "string" || !body.server_sig) {
    const err = new Error("resposta não assinada");
    err.response = res;
    throw err;
  }
  const ok = await verifyServer(body.data, body.server_sig, keys.verifyKey);
  if (!ok) throw new Error("assinatura do servidor inválida");
  const parsed = JSON.parse(body.data);
  parsed._status = res.status;
  return parsed;
}
